/**
 * PHARMAKON — pricing engine · the quantities of a purchase line under the facture's QTE reçue mode
 * (CP §3.1, D-25, A-74, A-75, A-99; refonte WP-63).
 *
 * One STORED meaning in both modes (A-74): `quantite` = paid (billed) units, `ug` = free units,
 * `quantite_recue` = paid units received; the stock entry is `quantite_recue + ug` (D-08 storage —
 * the engine `calculerLigne`, the lot ledger and every reader keep it). The mode only decides what the
 * operator types and reads:
 *
 *   mode              typed « Qté »         « Qté reçue » while derived     enters stock
 *   PAYEE_PLUS_UG     paid units            Qté + UG                        Qté reçue
 *   PAYEE_INCLUT_UG   paid units + UG       Qté                             Qté reçue
 *
 * In both modes « Qté reçue » is the number of units that enter the stock. When the owner lets it be
 * typed (`qte_recue_modifiable`), a shortfall is attributed to the paid units (A-75), so it never goes
 * below the UG (A-99), and a surplus is refused (A-75: a « Reçu non facturé » avoir declares it).
 *
 * UG % keeps the D-04 meaning in both modes: UG per 100 PAID units. In « UG incluses » the typed Qté
 * holds the UG, so a typed % gives UG = floor(Qté × % ÷ (100 + %)) — the largest UG with
 * UG ≤ (Qté − UG) × % ÷ 100, i.e. the A-01 floor on the paid units — and the back-computed % is
 * UG ÷ (Qté − UG). Every UG the D-04 rule gives on stored paid units satisfies the « incluses » rule
 * on their total, so a stored line reads back the same in either mode.
 *
 * Pure: no I/O, no settings lookup (the mode is the facture's snapshot, passed in).
 */
import { diviserPlancher, versCentiemesPct } from "./money.js";
import { ERREURS_QUANTITES_ACHAT, MAITRE_UG, MODE_QTE_RECUE } from "./constantes.js";
import { pctDepuisUg, ugDepuisPct } from "./achat-totaux.js";

const vide = (v) => v === null || v === undefined || v === "";

/**
 * The mode a computation runs in: a known mode as given, anything else « Qté payée + UG » — the
 * meaning every row stored before M-19 has (its back-fill) and the owner's default (D-25).
 */
export function modeQteRecue(mode) {
  return mode === MODE_QTE_RECUE.PAYEE_INCLUT_UG ? MODE_QTE_RECUE.PAYEE_INCLUT_UG : MODE_QTE_RECUE.PAYEE_PLUS_UG;
}

/** True for « Qté payée, UG incluses » (the typed Qté holds the UG). */
export function ugIncluses(mode) {
  return modeQteRecue(mode) === MODE_QTE_RECUE.PAYEE_INCLUT_UG;
}

/** A non-negative integer of an entry being typed (empty / unreadable → 0). */
function entier(v) {
  if (vide(v)) return 0;
  const n = typeof v === "number" ? v : Number(String(v).trim());
  return Number.isSafeInteger(n) && n >= 0 ? n : 0;
}

/**
 * UG of a typed UG % (`pctCentiemes`, hundredths) on the typed Qté, in the facture's mode — the A-01
 * floor on the paid units in both modes.
 */
export function ugDepuisPctSaisie(mode, quantiteSaisie, pctCentiemes) {
  if (!(pctCentiemes > 0)) return 0;
  if (!ugIncluses(mode)) return ugDepuisPct(quantiteSaisie, pctCentiemes);
  return diviserPlancher(BigInt(quantiteSaisie) * BigInt(pctCentiemes), 10000n + BigInt(pctCentiemes));
}

/**
 * UG % (hundredths, 2 decimals) back-computed from a typed UG on the typed Qté — UG per 100 paid units
 * (D-04) — or null while there is no paid unit.
 */
export function pctDepuisUgSaisie(mode, ug, quantiteSaisie) {
  const payees = ugIncluses(mode) ? quantiteSaisie - ug : quantiteSaisie;
  return payees > 0 ? pctDepuisUg(ug, payees) : null;
}

/**
 * True when a typed UG % still gives the stored UG of a line in the facture's mode (the server keeps
 * the % as typed rather than the back-computed one — A-01; « incluses » rule in « UG incluses »).
 *
 * @param {string} mode
 * @param {{ quantite: number, ug: number }} stockee paid units and UG as stored
 * @param {number} pctCentiemes
 */
export function pctExpliqueUg(mode, { quantite, ug }, pctCentiemes) {
  if (!(pctCentiemes >= 0)) return false;
  if (!ugIncluses(mode)) return ugDepuisPct(quantite, pctCentiemes) === ug;
  return ugDepuisPctSaisie(mode, quantite + ug, pctCentiemes) === ug;
}

/**
 * The engine input (`calculerLigne`) of a line in the STORED vocabulary — a request line or a stored
 * row — under the facture's mode. In « Qté payée + UG » it is the line as given (D-04: a UG % master
 * gives UG = floor(Qté payée × % ÷ 100)). In « UG incluses » a UG % was typed on the Qté that holds
 * the UG, so the UG it gave cannot be re-derived from the paid units alone (Qté 21 at 10 % → 1 UG on
 * 20 paid; 22 → 2 UG on 20 paid): a UG % master line that carries the UG it gave, and whose % still
 * gives it under the « incluses » rule, is computed on that UG (the typed % is then stored as typed,
 * `pctExpliqueUg`). Any other line is computed as given — a UG % alone gives the D-04 UG on the paid
 * units, which the « incluses » rule always accepts. Never throws: the engine reports what is unreadable.
 *
 * @param {string} mode
 * @param {object} ligne the line (Prisma column names + `maitre_ug`)
 * @returns {object} the line to compute (the same object when nothing changes)
 */
export function entreeSelonMode(mode, ligne) {
  if (!ligne || !ugIncluses(mode) || ligne.maitre_ug !== MAITRE_UG.UG_PCT || vide(ligne.ug) || vide(ligne.ug_pct)) {
    return ligne;
  }
  const quantite = Number(ligne.quantite);
  const ug = Number(ligne.ug);
  if (!Number.isSafeInteger(quantite) || !Number.isSafeInteger(ug) || quantite < 1 || ug < 0) return ligne;
  let pct;
  try {
    pct = versCentiemesPct(ligne.ug_pct);
  } catch {
    return ligne;
  }
  return pctExpliqueUg(mode, { quantite, ug }, pct) ? { ...ligne, maitre_ug: MAITRE_UG.UG } : ligne;
}

/** « Qté reçue » while it is derived: the units the mode makes enter the stock. */
export function quantiteRecueAttendue(mode, quantiteSaisie, ug) {
  return ugIncluses(mode) ? quantiteSaisie : quantiteSaisie + ug;
}

/**
 * The stored quantities of a line from what the operator typed, in the facture's mode (D-25, A-74,
 * A-75, A-99). The UG master rule is A-01 (the field typed last wins).
 *
 * @param {string} mode the facture's QTE reçue mode
 * @param {object} saisie
 * @param {number|string} saisie.quantite        the typed « Qté » (paid units; paid + UG in « UG incluses »)
 * @param {number|string} [saisie.ug]            the typed UG (master when `maitre_ug` = UG)
 * @param {number} [saisie.ug_pct_centiemes]     the typed UG % in hundredths (master when `maitre_ug` = UG_PCT)
 * @param {"UG"|"UG_PCT"} [saisie.maitre_ug]
 * @param {number|string|null} [saisie.quantite_recue] the typed « Qté reçue » (units entering stock);
 *   empty = derived
 * @returns {{ quantite: number, ug: number, quantite_recue: number, recue_attendue: number,
 *   recue: number, manquant: number, entree_stock: number, erreurs: string[] }}
 *   `quantite` / `ug` / `quantite_recue` are the stored columns (paid, free, paid received);
 *   `recue_attendue` the derived Qté reçue, `recue` the Qté reçue kept, `manquant` the paid units
 *   short. `erreurs` lists ERREURS_QUANTITES_ACHAT codes; the columns are clamped so a preview still reads.
 */
export function quantitesStockees(mode, saisie = {}) {
  const erreurs = [];
  const q = entier(saisie.quantite);
  const ug = saisie.maitre_ug === MAITRE_UG.UG_PCT
    ? ugDepuisPctSaisie(mode, q, saisie.ug_pct_centiemes ?? 0)
    : entier(saisie.ug);
  let payees = q;
  if (ugIncluses(mode)) {
    payees = q - ug;
    if (payees < 1) {
      erreurs.push(ERREURS_QUANTITES_ACHAT.UG_SANS_UNITE_PAYEE);
      payees = Math.max(0, payees);
    }
  }
  const attendue = payees + ug;
  let recue = vide(saisie.quantite_recue) ? attendue : entier(saisie.quantite_recue);
  if (recue > attendue) {
    erreurs.push(ERREURS_QUANTITES_ACHAT.QUANTITE_RECUE_SUPERIEURE);
    recue = attendue;
  } else if (recue < ug) {
    erreurs.push(ERREURS_QUANTITES_ACHAT.QUANTITE_RECUE_INFERIEURE_AUX_UG);
    recue = ug;
  }
  return {
    quantite: payees,
    ug,
    quantite_recue: recue - ug,
    recue_attendue: attendue,
    recue,
    manquant: attendue - recue,
    entree_stock: recue,
    erreurs,
  };
}

/**
 * What the entry shows of a STORED line, in the facture's mode: the typed « Qté », the UG, the
 * « Qté reçue » (units entering stock), the derived one and the paid units short.
 *
 * @param {string} mode
 * @param {{ quantite: number|string, ug?: number|string, quantite_recue?: number|string|null }} ligne
 *   stored columns; `quantite_recue` absent = fully received
 */
export function quantitesSaisies(mode, ligne = {}) {
  const payees = entier(ligne.quantite);
  const ug = entier(ligne.ug);
  const recuesPayees = vide(ligne.quantite_recue) ? payees : entier(ligne.quantite_recue);
  const attendue = payees + ug;
  return {
    quantite: ugIncluses(mode) ? payees + ug : payees,
    ug,
    quantite_recue: recuesPayees + ug,
    recue_attendue: attendue,
    manquant: Math.max(0, payees - recuesPayees),
  };
}
